import { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FoodsTabs } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { CONSENT_GOVERNANCE_FEATURE_SWITCH } from "src/api/client-api/consent/constants";
import { LanguageSelector } from "src/components/Selector/LanguageSelector/LanguageSelector";
import { ConsentPreferencesPanel, ContractualAcceptancesPanel } from "./ConsentSections";
import { PersonalInfoTab } from "./PersonalInfoTab";

const PROFILE_TAB_PARAM = "tab" as const;
const PROFILE_INFO_TAB = "info" as const;
const PROFILE_PREFERENCES_TAB = "preferences" as const;
const PROFILE_ACCEPTANCES_TAB = "acceptances" as const;

type ProfileSettingsTabId = typeof PROFILE_INFO_TAB | typeof PROFILE_PREFERENCES_TAB | typeof PROFILE_ACCEPTANCES_TAB;

type ProfileSettingsTab = {
    id: ProfileSettingsTabId;
    name: string;
};

export const ProfileSettingsPage = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isFeatureActivated } = useApplicationParameters();
    const isConsentGovernanceEnabled = isFeatureActivated(CONSENT_GOVERNANCE_FEATURE_SWITCH);

    const tabs = useMemo(() => {
        const items: ProfileSettingsTab[] = [
            {
                id: PROFILE_INFO_TAB,
                name: t("profileSettings.tabs.info", "Information and credentials"),
            },
            {
                id: PROFILE_PREFERENCES_TAB,
                name: t("profileSettings.tabs.preferences", "Preferences"),
            },
        ];

        if (isConsentGovernanceEnabled) {
            items.push({
                id: PROFILE_ACCEPTANCES_TAB,
                name: t("profileSettings.tabs.acceptances", "Contractual acceptances"),
            });
        }

        return items;
    }, [isConsentGovernanceEnabled, t]);

    const selectedTabId =
        tabs.some((tab) => tab.id === searchParams.get(PROFILE_TAB_PARAM)) ?
            ((searchParams.get(PROFILE_TAB_PARAM) as ProfileSettingsTabId | null) ?? PROFILE_INFO_TAB)
        :   (tabs[0]?.id ?? PROFILE_INFO_TAB);
    const selectedTabIndex = Math.max(
        tabs.findIndex((tab) => tab.id === selectedTabId),
        0,
    );

    const switchToTab = (tabId: string) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set(PROFILE_TAB_PARAM, tabId);
        setSearchParams(nextParams, { replace: true });
    };

    return (
        <Box sx={{ p: { xs: theme.spacing(2), md: theme.spacing(6) }, mx: "auto", maxWidth: "100%" }}>
            <Stack gap={theme.spacing(4)}>
                <Stack gap={theme.spacing(1)}>
                    <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
                        {t("profileSettings.title", "Profile settings")}
                    </Typography>
                    <Typography variant="body" sx={{ color: theme.custom.grey[2000], maxWidth: "780px" }}>
                        {t(
                            "profileSettings.description",
                            "Manage your personal information, preferences, and contractual acceptances from a dedicated profile area.",
                        )}
                    </Typography>
                </Stack>

                <FoodsTabs
                    tabs={tabs.map((tab) => ({
                        id: tab.id,
                        name: tab.name,
                        TabProps: { onClick: () => switchToTab(tab.id) },
                    }))}
                    tabVariant="primary"
                    selectedTabIndex={selectedTabIndex}
                    forceScrollable
                    BoxProps={{
                        sx: {
                            bgcolor: "transparent",
                            boxShadow: "none",
                            border: "none",
                            borderRadius: 0,
                            p: 0,
                        },
                    }}
                    TabsProps={{
                        sx: {
                            minHeight: "44px",
                            "& .MuiTabs-indicator": {
                                height: "3px",
                                borderRadius: 0,
                            },
                            "& .MuiTab-root": {
                                minHeight: "44px",
                                bgcolor: "transparent !important",
                                boxShadow: "none !important",
                            },
                        },
                    }}
                />

                {selectedTabId === PROFILE_INFO_TAB && <PersonalInfoTab />}

                {selectedTabId === PROFILE_PREFERENCES_TAB && (
                    <Stack gap={theme.spacing(3)}>
                        <Box
                            sx={{
                                backgroundColor: "white",
                                border: `1px solid ${theme.custom.grey[600]}`,
                                borderRadius: theme.spacing(2),
                                p: theme.spacing(4),
                                boxShadow: theme.custom.shadows.grey,
                            }}
                        >
                            <Stack gap={theme.spacing(2)}>
                                <Typography variant="h3" sx={{ fontSize: "20px", fontWeight: 700 }}>
                                    {t("profileSettings.preferences.language.title", "Language preferences")}
                                </Typography>
                                <Typography variant="body" sx={{ color: theme.custom.grey[2000], maxWidth: "620px" }}>
                                    {t(
                                        "profileSettings.preferences.language.description",
                                        "Choose the language used in your personal area.",
                                    )}
                                </Typography>
                                <Box sx={{ maxWidth: "320px" }}>
                                    <LanguageSelector />
                                </Box>
                            </Stack>
                        </Box>

                        {isConsentGovernanceEnabled && <ConsentPreferencesPanel />}
                    </Stack>
                )}

                {selectedTabId === PROFILE_ACCEPTANCES_TAB && isConsentGovernanceEnabled && (
                    <ContractualAcceptancesPanel />
                )}
            </Stack>
        </Box>
    );
};
